<template>
  <div class="page">
    <header class="page-header">
      <div class="page-header-bar">绑定终端</div>
      <div class="page-header-content">
        <h1>第三步：扫描设备码</h1>
        <p>确认定位信息，扫码绑定设备</p>
      </div>
    </header>

    <main class="page-body">
      <div class="page-body-scroll">
        <!-- 1. 定位信息卡片 -->
        <section class="panel info-panel">
          <div class="panel-head">
            <div class="panel-title">定位信息</div>
            <van-button plain size="small" type="primary" @click="goLocate">调整定位</van-button>
          </div>

          <div class="info-list">
            <div class="info-item">
              <span class="info-label">所属区域：</span>
              <span class="info-value">{{ areaName }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">所属楼宇：</span>
              <span class="info-value">{{ building }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">所属电梯井：</span>
              <span class="info-value">{{ shaft }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">设备类型：</span>
              <span class="info-value">{{ deviceType }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">安装位置：</span>
              <span class="info-value">{{ installPosition }}</span>
            </div>
          </div>
        </section>

        <!-- 2. 扫码区域 -->
        <section class="panel scan-panel">
          <div class="panel-title">
            设备编码
            <span class="scan-tip">请扫码或手动输入设备码</span>
          </div>

          <div class="status-text" :class="{ success: deviceCode }">
            {{ statusText }}
          </div>

          <!-- 3. 设备编码结果 -->
          <div class="device-result">
            <span class="device-label">设备编码：</span>
            <span class="device-code">{{ deviceCode }}</span>
          </div>

          <div class="scan-actions">
            <van-button plain size="small" type="primary" @click="goScanCode">
              {{ hasScanResult ? '重新扫码' : '扫码设备码' }}
            </van-button>
            <van-button plain size="small" type="primary" @click="openManualDialog">手动输入</van-button>
          </div>
        </section>
      </div>
    </main>

    <footer class="page-footer safe-bottom">
      <van-button block type="primary" :disabled="!deviceCode || binding" :loading="binding" @click="goNext">确认绑定</van-button>
    </footer>

    <div v-if="showManualDialog" class="dialog-mask" @click="closeManualDialog">
      <div class="dialog-card" @click.stop>
        <div class="dialog-title">手动输入设备码</div>
        <van-field
          v-model="manualCodeInput"
          class="dialog-field"
          placeholder="请输入设备码或 SN: 开头的设备码"
          clearable
          autocomplete="off"
        />
        <div class="dialog-tip">条码无法识别时，可手动输入后继续绑定</div>
        <div class="dialog-actions">
          <button type="button" class="dialog-button dialog-button-secondary" @click="closeManualDialog">取消</button>
          <button type="button" class="dialog-button dialog-button-primary" @click="applyManualCode">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { bindTerminal } from '@/api/install'
import { useMainStore } from '@/store'
import { normalizeTerminalCode } from './terminal-code'

const router = useRouter()
const store = useMainStore()
const binding = ref(false)
const showManualDialog = ref(false)
const manualCodeInput = ref('')

if (!store.installContext) {
  router.replace('/install/area')
} else if (!store.installSelection.terminalId) {
  router.replace('/install/location')
}

const areaName = computed(() => store.installContext?.regionName || '')
const building = computed(() => store.installSelection.buildingName)
const shaft = computed(() => store.installSelection.shaftName)
const deviceType = computed(() => store.installSelection.deviceType)
const installPosition = computed(() => store.installSelection.installPosition)
const deviceCode = computed(() => store.installSelection.terminalCode)
const hasScanResult = computed(() => Boolean(deviceCode.value))
const statusText = computed(() => {
  if (deviceCode.value) {
    return '设备码识别成功'
  }

  return '请先扫码或手动输入设备码'
})

const goLocate = () => {
  router.push('/install/location')
}

const goScanCode = () => {
  router.push('/install/device/scan')
}

const openManualDialog = () => {
  manualCodeInput.value = deviceCode.value
  showManualDialog.value = true
}

const closeManualDialog = () => {
  showManualDialog.value = false
}

const applyManualCode = () => {
  const terminalCode = normalizeTerminalCode(manualCodeInput.value)

  if (!terminalCode) {
    showToast('设备码无效，请重新输入')
    return
  }

  store.setInstallSelection({ terminalCode })
  manualCodeInput.value = terminalCode
  closeManualDialog()
  showToast({ type: 'success', message: '设备码填写成功' })
}

const goNext = () => {
  void submitBind()
}

const submitBind = async () => {
  if (!store.installSelection.terminalId) {
    showToast('缺少终端信息，请返回上一步重新选择')
    router.replace('/install/location')
    return
  }
  if (!deviceCode.value) {
    showToast('请先扫码或手动输入设备码')
    return
  }

  binding.value = true
  try {
    await bindTerminal({
      terminalId: store.installSelection.terminalId,
      terminalCode: deviceCode.value
    })
    router.push('/install/success')
  } catch (error) {
    showToast(error instanceof Error ? error.message : '绑定失败，请稍后重试')
  } finally {
    binding.value = false
  }
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  overflow: hidden;
  box-sizing: border-box;
  background: #f7f8fa;
  padding-top: calc(env(safe-area-inset-top) + 48px);
}

.page-header {
  flex: none;
  text-align: center;
}

.page-header-bar {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(env(safe-area-inset-top) + 48px);
  padding-top: env(safe-area-inset-top);
  box-sizing: border-box;
  background: #3e87f8;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
}

.page-header-content {
  padding: 16px 0 8px;
}

.page-header h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1.4;
  color: #1f2937;
}

.page-header p {
  margin: 8px 0 0;
  font-size: 14px;
  color: #6b7280;
}

.page-body {
  flex: 1;
  min-height: 0;
  margin: 8px 10px 0;
  padding: 16px;
  overflow: hidden;
  box-sizing: border-box;
  border-radius: 16px;
  background: #eef0f4;
}

.page-body-scroll {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.page-body-scroll::-webkit-scrollbar {
  display: none;
}

.panel {
  padding: 16px;
  border-radius: 8px;
  background: #fff;
}

.panel-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: #1f2937;
}

.scan-tip {
  min-width: 0;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.4;
  color: #9ca3af;
}

/* 定位信息卡片 */
.info-panel .panel-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.info-label {
  flex: none;
  font-size: 14px;
  color: #6b7280;
}

.info-value {
  min-width: 0;
  flex: 1;
  font-size: 16px;
  line-height: 1.5;
  word-break: break-all;
  color: #111827;
}

.info-panel :deep(.van-button) {
  flex: none;
  min-width: 88px;
  border-color: #3e87f8;
  border-radius: 999px;
}

/* 扫码区域 */
.scan-panel {
  padding: 16px;
}

.scan-panel .panel-title {
  margin-bottom: 12px;
}

.status-text {
  margin: 0;
  font-size: 16px;
  text-align: center;
  color: #6b7280;
}

.status-text.success {
  color: #3e87f8;
}

/* 设备编码结果 */
.device-result {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 14px;
  border-radius: 8px;
  background: #f8fafc;
}

.device-label {
  flex: none;
  font-size: 12px;
  color: #9ca3af;
}

.device-code {
  min-width: 0;
  flex: 1;
  font-size: 16px;
  text-align: right;
  word-break: break-all;
  color: #111827;
}

.scan-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.scan-actions :deep(.van-button) {
  border-color: #3e87f8;
  border-radius: 999px;
}

.dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.42);
}

.dialog-card {
  width: min(100%, 420px);
  padding: 20px 16px 16px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.18);
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  color: #1f2937;
}

.dialog-field {
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
}

.dialog-field :deep(.van-field__control) {
  font-size: 14px;
}

.dialog-tip {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  margin-top: 18px;
}

.dialog-button {
  flex: 1;
  height: 40px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
}

.dialog-button-secondary {
  border-color: #d1d5db;
  background: #fff;
  color: #4b5563;
}

.dialog-button-primary {
  background: #3e87f8;
  color: #fff;
}

.page-footer {
  flex: none;
  padding: 8px 16px 0;
}

.safe-bottom {
  padding-bottom: calc(env(safe-area-inset-bottom) + 16px);
}
</style>
